const ACTIONABLE =
  'button, a, [role="button"], [role="tab"], [role="menuitem"], [role="option"], ' +
  '.ant-tabs-tab, .ant-select-item, .ant-dropdown-menu-item, .ant-menu-item, li, label';

const esc = (s) => (window.CSS?.escape ? window.CSS.escape(s) : String(s).replace(/([^\w-])/g, '\\$1'));

const attrSelector = (attr, value) => `[${attr}="${String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"]`;

const firstLine = (el) => (el.innerText || el.textContent || '').trim().split('\n')[0].trim();

const stableId = (el) => (el.id && !/^(rc[-_]|:r)|\d{3,}/.test(el.id) ? el.id : null);

const innermost = (list) => list.filter((el) => !list.some((o) => o !== el && el.contains(o)));

const findByText = (doc, text) => {
  const strict = Array.from(doc.querySelectorAll(ACTIONABLE)).filter((el) => firstLine(el) === text);
  if (strict.length) return strict;
  return Array.from(doc.querySelectorAll('span, div')).filter(
    (el) => el.children.length === 0 && firstLine(el) === text
  );
};

const findByIcon = (doc, iconClass) =>
  innermost(
    Array.from(doc.querySelectorAll(ACTIONABLE)).filter((el) => el.querySelector(`.${esc(iconClass)}`))
  );

const cssPath = (el) => {
  const parts = [];
  for (let n = el; n && n.nodeType === 1 && n.tagName !== 'BODY'; n = n.parentElement) {
    if (stableId(n)) {
      parts.unshift(`#${esc(n.id)}`);
      break;
    }
    let part = n.tagName.toLowerCase();
    const same = Array.from(n.parentElement?.children || []).filter((c) => c.tagName === n.tagName);
    if (same.length > 1) part += `:nth-of-type(${same.indexOf(n) + 1})`;
    parts.unshift(part);
  }
  return parts.join(' > ');
};

const isUnique = (doc, sel) => {
  try {
    return doc.querySelectorAll(sel).length === 1;
  } catch (e) {
    return false;
  }
};

export const buildLocator = (rawTarget) => {
  const el = rawTarget.closest?.(ACTIONABLE) || rawTarget;
  const doc = el.ownerDocument;
  const text = firstLine(el);
  const candidates = [];

  for (const attr of ['data-testid', 'data-test', 'data-cy']) {
    const v = el.getAttribute(attr);
    if (v && isUnique(doc, attrSelector(attr, v))) {
      candidates.push({ kind: 'css', value: attrSelector(attr, v) });
    }
  }
  if (stableId(el) && isUnique(doc, `#${esc(el.id)}`)) {
    candidates.push({ kind: 'css', value: `#${esc(el.id)}` });
  }

  let a11yLabel = '';
  for (const attr of ['aria-label', 'title', 'name']) {
    const v = el.getAttribute(attr);
    if (v) {
      a11yLabel = a11yLabel || v;
      const sel = attrSelector(attr, v);
      if (isUnique(doc, sel)) candidates.push({ kind: 'css', value: sel });
    }
  }

  if (text) {
    const idx = findByText(doc, text).indexOf(el);
    candidates.push({ kind: 'text', value: text, index: Math.max(idx, 0) });
  }

  const iconEl = el.querySelector?.('[class*="anticon-"]');
  const iconClass = iconEl && Array.from(iconEl.classList).find((c) => /^anticon-(?!spin)/.test(c));
  if (iconClass) {
    const idx = findByIcon(doc, iconClass).indexOf(el);
    if (idx >= 0) candidates.push({ kind: 'icon', value: iconClass, index: idx });
  }

  candidates.push({ kind: 'css', value: cssPath(el) });

  const label =
    text || a11yLabel || (iconClass ? `${iconClass.replace('anticon-', '')} 圖示按鈕` : '') || '新增步驟';

  return { label, text, locator: { candidates } };
};

export const resolveLocator = (doc, locator, exclude = () => false) => {
  for (const c of locator?.candidates || []) {
    let list = [];
    try {
      if (c.kind === 'text') list = findByText(doc, c.value);
      else if (c.kind === 'icon') list = findByIcon(doc, c.value);
      else list = Array.from(doc.querySelectorAll(c.value));
    } catch (e) {
      continue;
    }
    const el = list.filter((e) => !exclude(e))[c.index ?? 0];
    if (el) return el;
  }
  return null;
};